import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { CircularProgress, Button, TextField, Select, MenuItem } from '@mui/material';
import ExcelJS from 'exceljs';


const DistributionStatusReportFps = ({ distCode, status, month, year }) => {
  const [data, setData] = useState(null); // full response
  const [list, setList] = useState([]); // array of rows
  const [loadingFps, setLoadingFps] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [downloading, setDownloading] = useState(false);
  const detailsRef = useRef(null);

  // derived filtered list
  const filtered = (list || []).filter((row) => {
    if (!search) return true;
    const term = search.trim().toLowerCase();
    // search across a few useful fields
    return (
      String(row.fps_id || '').toLowerCase().includes(term) ||
      String(row.del_name || '').toLowerCase().includes(term) ||
      String(row.afso_name_en || '').toLowerCase().includes(term)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    if (distCode && status !== undefined && status !== null && month && year) {
      loadDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distCode, status, month, year]);

  useEffect(() => {
    // when filtered size changes ensure page is valid
    if (page > totalPages) setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered.length, pageSize]);

  useEffect(() => {
    // scroll into view when data is loaded
    if (data && detailsRef.current) {
      detailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [data]);

  const loadDetails = async () => {
    setLoadingFps(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_URL_SIDE}/api/fps`, {
        params: { distCode, month, year, status },
      });
      const responseData = res.data || {};
      setData(responseData);
      setList(responseData.list || []); // may be response.list or response.data.list
      setPage(1);
      setSearch('');
    } catch {
      alert('Failed to load FPS details');
      setData(null);
      setList([]);
    } finally {
      setLoadingFps(false);
    }
  };


  const excelDownloadFps = async () => {
    try {
      setDownloading(true);

      // Get the FPS data
      const response = await axios.get(
        `${process.env.REACT_APP_URL_SIDE}/api/fps`,
        {
          params: {
            distCode,
            month,
            year,
            status
          },
          responseType: 'json',
        }
      );

      if (!response.data) {
        alert('No data received from server');
        return;
      }

      const fpsData = response.data.list || response.data || [];

      // Create workbook and worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('FPS Details');

      // Add headers
      worksheet.columns = [
        { header: 'Sl No', key: 'slNo', width: 10, style: { alignment: { horizontal: 'center' } } },
        { header: 'Office', key: 'office', width: 25 },
        { header: 'FPS ID', key: 'fpsId', width: 15 },
        { header: 'Dealer Name', key: 'dealer', width: 30 },
        { header: 'Total Cards', key: 'totalCards', width: 15, style: { alignment: { horizontal: 'right' } } },
        { header: 'Online Cards', key: 'onlineCards', width: 15, style: { alignment: { horizontal: 'right' } } },
        { header: 'Partial Offline', key: 'partialOffline', width: 18, style: { alignment: { horizontal: 'right' } } },
        { header: '% Availed', key: 'onlinePercent', width: 15, style: { alignment: { horizontal: 'right' } } },
        { header: 'Portability', key: 'portability', width: 15, style: { alignment: { horizontal: 'right' } } },
        { header: 'Total %', key: 'totalPercent', width: 12, style: { alignment: { horizontal: 'right' } } }
      ];

      // Style the header row
      worksheet.getRow(1).font = { bold: true };
      worksheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE6E6FA' }
      };

      // Add title row
      worksheet.insertRow(1, [`FPS Distribution Details - ${data.dist_name_en} - ${data.msg}`]);
      worksheet.mergeCells('A1:J1');
      worksheet.getRow(1).font = { bold: true, size: 14 };
      worksheet.getRow(1).alignment = { horizontal: 'center' };

      // Add data rows
      fpsData.forEach((item, index) => {
        worksheet.addRow({
          slNo: index + 1,
          office: item.afso_name_en || '',
          fpsId: item.fps_id || '',
          dealer: item.del_name || '',
          totalCards: item.total_cards || 0,
          onlineCards: item.avail_online_cards || 0,
          partialOffline: item.avail_poff_cards || 0,
          onlinePercent: item.online_trans_per || 0,
          portability: item.avail_port_cards || 0,
          totalPercent: item.trans_per || 0
        });
      });

      // Add totals if available in response
      if (response.data.tot_cards) {
        worksheet.addRow({});
        worksheet.addRow({
          dealer: 'TOTAL',
          totalCards: response.data.tot_cards,
          onlineCards: response.data.online_month_card,
          partialOffline: response.data.poff_month_card,
          onlinePercent: response.data.tot_online_per,
          portability: response.data.port_month_card,
          totalPercent: response.data.tot_per
        });

        const totalsRow = worksheet.lastRow;
        totalsRow.font = { bold: true };
        totalsRow.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF0F8FF' }
        };
      }

      // Generate Excel file
      const buffer = await workbook.xlsx.writeBuffer();

      // Create and download file
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fps_${distCode}_${month}_${year}_status_${status}.xlsx`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

    } catch (error) {
      console.error('FPS Excel download error:', error);
      alert(`Download failed: ${error.message}`);
    } finally {
      setDownloading(false);
    }
  };

  if (loadingFps) return <CircularProgress style={{ display: 'block', margin: '40px auto' }} />;
  if (!data || (Array.isArray(list) && list.length === 0))
    return (
      <div ref={detailsRef} className="response" style={{ textAlign: 'center', marginTop: 20 }}>
        No Data Found
      </div>
    );

  const { msg, dist_name_en } = data;

  return (
    <div ref={detailsRef} id="detailsSection" style={{ width: '95%', margin: '0 auto' }}>
      <table className="report-table" style={{ width: '100%', marginBottom: 8 }}>
        <thead>
          <tr className="tableheader" >
            <th colSpan={10} style={{ textAlign: 'center', padding: '8px' }}>
              Shops with Distribution <strong>{msg}</strong> in <strong>{dist_name_en}</strong>
            </th>
          </tr>
        </thead>
      </table>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
        <TextField
          label="Search (FPS / Dealer / Office)"
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ flex: 1 }}
        />

        <div>
          <Select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            size="small"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="contained" onClick={excelDownloadFps}>
            Export Excel
          </Button>

        </div>
      </div>

      <div style={{ overflow: 'auto', scrollBehavior: 'smooth', border: '1px solid #eee' }}>
        <table id="Report" className="report-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr className="tableheader">
              <th>Sl No</th>
              <th>Office</th>
              <th>FPS</th>
              <th>Dealer</th>
              <th style={{ textAlign: 'right' }}>Total Cards (A)</th>
              <th style={{ textAlign: 'right' }}>Online (B)</th>
              <th style={{ textAlign: 'right' }}>Partial Offline (C)</th>
              <th style={{ textAlign: 'right' }}>% Availed</th>
              <th style={{ textAlign: 'right' }}>Portability (D)</th>
              <th style={{ textAlign: 'right' }}>Total %</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((item, i) => (
              <tr key={item.fps_id || `${i}-${item.del_name}`}>
                <td>{(page - 1) * pageSize + i + 1}</td>
                <td>{item.afso_name_en}</td>
                <td>{item.fps_id}</td>
                <td>{item.del_name}</td>
                <td align="right">{item.total_cards}</td>
                <td align="right">{item.avail_online_cards}</td>
                <td align="right">{item.avail_poff_cards}</td>
                <td align="right">{item.online_trans_per}</td>
                <td align="right">{item.avail_port_cards}</td>
                <td align="right">{item.trans_per}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="tableheader">
              <th colSpan={4}>Total</th>
              <th>{data.tot_cards}</th>
              <th>{data.online_month_card}</th>
              <th>{data.poff_month_card}</th>
              <th>{data.tot_online_per}</th>
              <th>{data.port_month_card}</th>
              <th>{data.tot_per}</th>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* pagination */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
        <div>
          Page {page} of {totalPages} — Showing {paginated.length} of {filtered.length} rows
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Button
            size="small"
            variant="outlined"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DistributionStatusReportFps;
